import { z } from 'zod';
import { roleSchema, type Role } from './roles.js';

export const connexionSchema = z.object({
  email: z.string().email().max(320),
  motDePasse: z.string().min(1).max(200),
});
export type Connexion = z.infer<typeof connexionSchema>;

/**
 * Exigences de mot de passe. Volontairement fondées sur la longueur plutôt que
 * sur une combinaison de classes de caractères : ce sont des comptes nominatifs
 * de bureau, et les règles de composition poussent aux mots de passe notés.
 */
export const LONGUEUR_MOT_DE_PASSE_MIN = 12;

export const motDePasseSchema = z
  .string()
  .min(LONGUEUR_MOT_DE_PASSE_MIN, `Au moins ${LONGUEUR_MOT_DE_PASSE_MIN} caractères`)
  .max(200);

/** Style d'affichage d'un compte (migration 014) : il suit la personne sur tous ses appareils. */
export const APPARENCES = ['clair', 'sombre'] as const;
export const apparenceSchema = z.enum(APPARENCES);
export type Apparence = z.infer<typeof apparenceSchema>;

export const LIBELLES_APPARENCE: Record<Apparence, string> = { clair: 'Clair', sombre: 'Sombre' };

export const changementApparenceSchema = z.object({ apparence: apparenceSchema });

export const utilisateurCreationSchema = z.object({
  email: z.string().email().max(320),
  nom: z.string().min(1).max(200),
  motDePasse: motDePasseSchema,
  role: roleSchema.default('rh'),
  apparence: apparenceSchema.default('clair'),
  voirTelephones: z.boolean().default(false),
  deplacerCandidature: z.boolean().default(false),
});

export const utilisateurMiseAJourSchema = z
  .object({
    nom: z.string().min(1).max(200),
    role: roleSchema,
    actif: z.boolean(),
    apparence: apparenceSchema,
    voirTelephones: z.boolean(),
    deplacerCandidature: z.boolean(),
    motDePasse: motDePasseSchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Aucun champ à modifier' });

export interface UtilisateurPublic {
  id: string;
  email: string;
  nom: string;
  role: Role;
  actif: boolean;
  apparence: Apparence;
  /** Droit donné par le super admin de voir le téléphone des candidats (utile au lecteur). */
  voirTelephones: boolean;
  /** Droit donné par le super admin de déplacer une candidature d'une annonce à une autre. */
  deplacerCandidature: boolean;
  derniereConnexion: string | null;
}

export interface ReponseConnexion {
  /** Jeton d'accès, 15 minutes, à garder en mémoire (jamais en localStorage). */
  jetonAcces: string;
  expireDans: number;
  utilisateur: UtilisateurPublic;
}

/** Durées de vie des jetons (docs/API.md : accès 15 min, refresh 7 j, rotation). */
export const DUREE_ACCES_SECONDES = 15 * 60;
export const DUREE_RAFRAICHISSEMENT_SECONDES = 7 * 24 * 60 * 60;
export const COOKIE_RAFRAICHISSEMENT = 'rh_refresh';

/**
 * En-tête posé par la file d'envoi du téléphone : l'identifiant du compte qui a
 * fait la saisie. La garde refuse la requête s'il ne désigne pas le titulaire
 * du jeton (403 UTILISATEUR_DIFFERENT).
 */
export const ENTETE_UTILISATEUR_ATTENDU = 'x-rh-utilisateur';

/**
 * Délai de grâce de la rotation (docs/API.md § Authentification) : un cookie
 * déjà tourné, présenté dans ce délai alors que son successeur n'a jamais
 * servi, est repris (réponse perdue, renouvellements croisés). Au-delà, c'est
 * un vol présumé : toutes les sessions du compte sont révoquées.
 */
export const DELAI_GRACE_ROTATION_SECONDES = 10 * 60;

/** Maillons de chaîne parcourus au plus pour trouver le successeur inutilisé. */
export const MAX_MAILLONS_ROTATION = 10;
